// Contractor wizard step 1 — find them on the vetting register. RON-5466 Phase 4.
//
// This is the structural difference between inviting a visitor and inviting a contractor. A visitor
// is ad-hoc: you type their name and email and that IS the record. A contractor is someone the
// company has already vetted, so the host does not type their details at all — they look them up,
// and the register decides whether the invite may proceed.
//
// Four outcomes, and only one of them continues straight to the visit details:
//
//   cleared      proceed
//   provisional  proceed, but the visit is flagged so reception does not admit them yet
//   blocked      stop — with the reason, because the host needs to know who to talk to
//   not found    offer to request vetting, which is a different flow and NOT a booking

import { useTranslation } from "react-i18next";

import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";
import { formatISOToLong } from "../visitorDateUtils.js";
import ContractorVettingCard from "./ContractorVettingCard.jsx";

const ContractorLookupStep = ({
  email,
  onEmailChange,
  onLookup,
  isSearching,
  hasSearched,
  contractor,
  error,
  todayISO,
  // The OPEN vetting request for this email (§4.3's `lookup.request`), or null.
  openRequest,
}) => {
  const { t } = useTranslation("common");

  const notFound = hasSearched && !contractor && !error;

  return (
    <>
      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_contractor_find")}</p>
        <p className="visitor-card__hint">{t("react_visitors_contractor_find_hint")}</p>

        <VisitorFormRow
          id="contractor-email"
          label={t("email_address")}
          required
          error={error ? t(error) : null}
        >
          <div className="visitor-lookup">
            <input
              id="contractor-email"
              type="email"
              placeholder={`${t("email_address")}*`}
              className={fieldClassName(email, error)}
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              // Enter is how anyone actually submits a single-field search.
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onLookup();
                }
              }}
            />
            <button
              type="button"
              className="visitor-btn visitor-btn--secondary"
              onClick={onLookup}
              disabled={isSearching || !email.trim()}
            >
              {t(isSearching ? "react_loading" : "search")}
            </button>
          </div>
        </VisitorFormRow>
      </div>

      {/* ONE CARD, tinted by the register's answer. It was three until 29-October-2026 — an
          identity card, this one, and for a provisional contractor a third holding a single
          sentence — which made one answer look like three findings. */}
      {contractor && (
        <ContractorVettingCard
          contractor={contractor}
          todayISO={todayISO}
          variant="result"
          openRequest={openRequest}
        />
      )}

      {/* Not being on the register is the normal case for someone new, so it reads as a next step
          rather than a failure.

          THE 'REQUEST VETTING' BUTTON IS NOT HERE. It lives in the wizard footer, where the
          prototype puts it and where every other step keeps its forward action — see
          `lookupMissed` in `ContractorInviteWizard`. This card states the situation; the footer
          offers the way out of it. */}
      {notFound && (
        <div className="visitor-card visitor-card--danger visitor-lookup-miss">
          {/* The prototype badges the result with an avatar — initials for a hit, '?' for a miss.
              Decorative: the title says the same thing in words. */}
          <span className="visitor-lookup-miss__mark" aria-hidden="true">
            ?
          </span>
          <div>
            <p className="visitor-card__title">
              {t("react_visitors_contractor_not_found")}
            </p>
            <p className="visitor-card__hint">
              {t("react_visitors_contractor_not_found_hint")}
            </p>
            {/* ALREADY ASKED. Without this the host presses Request vetting, fills the whole
                form, and is told "we've reminded them" — which is the first they hear that
                anybody had asked before. The button stays: asking again is allowed, and updates
                the open request rather than creating a second one. */}
            {openRequest?.requestedAt && (
              <p className="visitor-card__hint">
                {t("react_visitors_vetting_already_requested").replace(
                  "%s",
                  formatISOToLong(openRequest.requestedAt, t)
                )}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ContractorLookupStep;
